import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const CATEGORIES = {
  discovery: { label: 'Discovery', actions: ['discovery_saved'], colour: '#1A56A0' },
  icp: { label: 'ICP', actions: ['icp_generated', 'icp_saved'], colour: '#8B1A1A' },
  sequences: { label: 'Sequences', actions: ['sequence_generated', 'sequence_approved', 'sequence_pushed', 'sequence_edited'], colour: '#B7791F' },
  prospects: { label: 'Prospects', actions: ['prospect_imported', 'prospect_added', 'contact_added'], colour: '#276749' },
  client: { label: 'Client', actions: ['client_created'], colour: '#4A5568' },
  intelligence: { label: 'Intelligence', actions: ['intelligence_sweep', 'intelligence_sent'], colour: '#6B46C1' },
};

const ACTION_LABELS = {
  icp_generated: 'ICP Generated', icp_saved: 'ICP Saved',
  discovery_saved: 'Discovery Brief Saved', sequence_generated: 'Sequence Generated',
  sequence_approved: 'Sequence Approved', sequence_pushed: 'Pushed to ManyReach',
  sequence_edited: 'Sequence Edited', prospect_imported: 'Prospects Imported',
  prospect_added: 'Prospect Added', client_created: 'Client Created',
  contact_added: 'Contact Added', intelligence_sweep: 'Intelligence Sweep',
  intelligence_sent: 'Intelligence Digest Sent',
};


function getCategory(actionType) {
  for (const [key, cat] of Object.entries(CATEGORIES)) {
    if (cat.actions.includes(actionType)) return { key, ...cat };
  }
  return { key: 'other', label: 'Other', colour: '#888' };
}

function groupByDate(activities) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const thisWeek = new Date(today); thisWeek.setDate(thisWeek.getDate() - 7);
  const thisMonth = new Date(today); thisMonth.setDate(thisMonth.getDate() - 30);
  const groups = { Today: [], Yesterday: [], 'This week': [], 'This month': [], Earlier: [] };
  activities.forEach(a => {
    const d = new Date(a.created_at);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day.getTime() === today.getTime()) groups['Today'].push(a);
    else if (day.getTime() === yesterday.getTime()) groups['Yesterday'].push(a);
    else if (d >= thisWeek) groups['This week'].push(a);
    else if (d >= thisMonth) groups['This month'].push(a);
    else groups['Earlier'].push(a);
  });
  return Object.entries(groups).filter(([, items]) => items.length > 0);
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ts) {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ActivityLog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [activities, setActivities] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => { fetchClient(); fetchActivities(); }, [id]);

  async function fetchClient() {
    try { const res = await fetch(`${API_BASE}/clients/${id}`); setClient(await res.json()); } catch (err) {}
  }

  async function fetchActivities() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/activity`);
      setActivities(await res.json());
    } catch (err) { setError('Could not load activity log.'); }
  }

  const filtered = categoryFilter === 'all'
    ? activities
    : activities.filter(a => getCategory(a.action_type).key === categoryFilter);

  const grouped = groupByDate(filtered);

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← {client ? client.name : 'Back'}
        </button>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Activity Log</h1>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{client ? client.name : ''} · {activities.length} entries</p>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => setCategoryFilter('all')}
            style={{ padding: '0.3rem 0.85rem', borderRadius: '20px', border: '1px solid ' + (categoryFilter === 'all' ? '#8B1A1A' : '#D4CFC9'), background: categoryFilter === 'all' ? '#8B1A1A' : 'transparent', color: categoryFilter === 'all' ? '#fff' : '#888', fontSize: '0.78rem', cursor: 'pointer', fontWeight: categoryFilter === 'all' ? '500' : '400' }}>
            All ({activities.length})
          </button>
          {Object.entries(CATEGORIES).map(([key, cat]) => {
            const count = activities.filter(a => cat.actions.includes(a.action_type)).length;
            if (count === 0) return null;
            return (
              <button key={key} onClick={() => setCategoryFilter(key)}
                style={{ padding: '0.3rem 0.85rem', borderRadius: '20px', border: '1px solid ' + cat.colour, background: categoryFilter === key ? cat.colour : 'transparent', color: categoryFilter === key ? '#fff' : cat.colour, fontSize: '0.78rem', cursor: 'pointer', fontWeight: '500' }}>
                {cat.label} ({count})
              </button>
            );
          })}
        </div>

        {error && <p style={{ color: '#C53030', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>}

        {grouped.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#888', backgroundColor: '#fff', borderRadius: '10px', border: '0.5px solid #E8E4E0' }}>
            <p style={{ margin: 0, fontWeight: '500', color: '#4A5568' }}>No activity recorded yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>Actions taken on this client will appear here.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {grouped.map(([groupLabel, items]) => (
              <div key={groupLabel}>
                <p style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500', margin: '0 0 0.75rem' }}>{groupLabel}</p>
                <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                  {items.map((activity, i) => {
                    const cat = getCategory(activity.action_type);
                    return (
                      <div key={activity.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '0.9rem 1.25rem', borderBottom: i < items.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                        <div style={{ marginTop: '0.35rem', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cat.colour, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>{ACTION_LABELS[activity.action_type] || activity.action_type}</p>
                              <span style={{ fontSize: '0.68rem', color: cat.colour, backgroundColor: cat.colour + '18', padding: '0.1rem 0.45rem', borderRadius: '20px', fontWeight: '500' }}>{cat.label}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#AAA', whiteSpace: 'nowrap', flexShrink: 0 }}>{groupLabel === 'Today' || groupLabel === 'Yesterday' ? formatTime(activity.created_at) : formatDate(activity.created_at)}</p>
                          </div>
                          {activity.detail && <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#888', lineHeight: '1.5' }}>{activity.detail}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityLog;
